import {
  AfterViewInit,
  Component,
  ElementRef,
  NgZone,
  OnDestroy,
  ViewChild,
} from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { environment } from 'src/environments/environment';
import { SessionService } from '../auth/session/session.service';

/** Message types exchanged with the embedded AI-NOC app. */
const IDENTITY_REQUEST = 'ainoc.identity.request';
const IDENTITY_RESPONSE = 'ainoc.identity';

@Component({
  selector: 'dcc-ai-noc',
  templateUrl: './ai-noc.component.html',
  styleUrls: ['./ai-noc.component.scss'],
})
export class AiNocComponent implements AfterViewInit, OnDestroy {
  @ViewChild('aiNocFrame') private frame?: ElementRef<HTMLIFrameElement>;

  // bypassSecurityTrustResourceUrl is required because Angular blocks dynamic
  // iframe src bindings by default. The URL comes from the environment config,
  // not from user input, so this is safe.
  readonly aiNocUrl: SafeResourceUrl;

  constructor(
    sanitizer: DomSanitizer,
    private sessionService: SessionService,
    private zone: NgZone,
  ) {
    this.aiNocUrl = sanitizer.bypassSecurityTrustResourceUrl(
      environment.aiNocUrl,
    );
  }

  ngAfterViewInit(): void {
    // Not ngOnInit: the handler validates the sender against the iframe ViewChild,
    // which is only resolved by this point. Outside Angular's zone because replying
    // touches no template state, and this listener sees every message on the page.
    this.zone.runOutsideAngular(() =>
      window.addEventListener('message', this.onMessage),
    );
  }

  ngOnDestroy(): void {
    window.removeEventListener('message', this.onMessage);
  }

  /**
   * Hands the signed-in user's identity to the embedded AI-NOC app on request.
   *
   * AI-NOC cannot read this app's session — localStorage is per-origin and an
   * iframe does not change that — so it asks once its own bootstrap finishes.
   * That keeps the user from being prompted to sign in a second time.
   *
   * Only email and display name are sent, never the token: AI-NOC has no way to
   * verify it today, so sending it would put a credential on the wire for no gain.
   */
  private readonly onMessage = (event: MessageEvent): void => {
    // environment.aiNocUrl is a bare origin, so this is an exact origin match.
    if (event.origin !== environment.aiNocUrl) return;
    if (
      (event.data as { type?: string } | undefined)?.type !== IDENTITY_REQUEST
    )
      return;

    // Only the frame this component renders — another tab or frame on the same
    // origin can reach this window too, and must not be answered.
    const frameWindow = this.frame?.nativeElement?.contentWindow;
    if (!frameWindow || event.source !== frameWindow) return;

    const session = this.sessionService.getCurrentSession();
    if (!session) return;

    frameWindow.postMessage(
      { type: IDENTITY_RESPONSE, email: session.email, name: session.name },
      environment.aiNocUrl,
    );
  };
}
